import { useCallback } from "react";
import { useSelector } from "react-redux";
import { isNil } from "lodash";

import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";

import Debug from "@/react/debug";
import {
  createUnbindVmmsResponseHandler,
  useUnbindVmmsMutation,
} from "@/react/features/support-tools/unbind-vmms/unbindVmmsApi";
import { errorNotification } from "@/react/notifications";
import {
  customerIdSelector,
  pbxIdSelector,
  pbxNameSelector,
} from "@/react/features/customer/customerSlice";

export const useDidActions = (setErrorState, setUnbindData) => {
  const sanitizeDid = useCallback(
    (rawDid) => {
      // Remove any acceptable characters
      let pendingDid = rawDid
        .replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll(" ", "")
        .replaceAll("(", "")
        .replaceAll(")", "");
      // Including a leading '+'
      pendingDid = pendingDid.startsWith("+")
        ? pendingDid.substring(1)
        : pendingDid;
      // Ensure that only 8+ digits remain
      const valid = pendingDid.match(/^\d{8,}$/g) != null;
      setErrorState(!valid);
      return { valid, sanitizedDid: valid ? pendingDid : null };
    },
    [setErrorState]
  );

  const [unbindVMMS, { isLoading: processingUnbindRequest }, error] =
    useUnbindVmmsMutation();
  const customerId = useSelector(customerIdSelector);
  const pbxId = useSelector(pbxIdSelector);
  const pbxName = useSelector(pbxNameSelector);

  const unbindDid = useCallback(
    (did) => {
      if (isNil(customerId) || customerId === "") {
        notify({
          type: TYPE.error,
          content: "Please select customer before making unbind request",
          duration: DURATION.INFINITE,
        });
        return;
      }
      const data = {
        customerId: customerId,
        pbxId: pbxId,
        pbxName: pbxName,
        phoneNumber: did,
      };
      Debug.log("Sending unbind request", data);
      unbindVMMS(data)
        .unwrap()
        .then(createUnbindVmmsResponseHandler({ setData: setUnbindData }))
        .catch(errorNotification);
    },
    [setUnbindData, customerId, pbxId, pbxName, unbindVMMS]
  );

  return {
    sanitizeDid,
    unbindDid,
    processingUnbindRequest,
    error,
  };
};
